import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { bomAPI } from '../../services/api';
import { printStyles, CompanyLetterhead } from './shared';

const money = (value, digits = 4) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

export default function BomKitCostSheetPrintView() {
  const { id } = useParams();
  const [bom, setBom] = useState(null);
  const [printTriggered, setPrintTriggered] = useState(false);
  const isPreviewMode = new URLSearchParams(window.location.search).get('preview') === 'true';

  useEffect(() => { bomAPI.get(id).then((response) => setBom(response.data)); }, [id]);

  useEffect(() => {
    if (bom && !printTriggered && !isPreviewMode) {
      setPrintTriggered(true);
      const timer = setTimeout(() => window.print(), 600);
      return () => clearTimeout(timer);
    }
  }, [bom, printTriggered, isPreviewMode]);

  if (!bom) {
    return (
      <div className="loading-container" data-testid="bom-cost-sheet-loading">
        <style>{printStyles}</style>
        <div className="spinner" />
      </div>
    );
  }

  const pricing = bom.pricing || {};

  return (
    <div className="print-container" data-testid="bom-cost-sheet-print-view">
      <style>{printStyles}</style>
      {!isPreviewMode && (
        <button className="download-pdf-btn no-print" onClick={() => window.print()} data-testid="bom-cost-sheet-print-button">
          Print / Save PDF
        </button>
      )}

      <CompanyLetterhead documentType="Kit Cost Sheet" documentNumber={`BOM V${bom.version_number}`} />
      <div className="confidential-banner" data-testid="bom-cost-sheet-internal-banner">
        INTERNAL DOCUMENT — NOT FOR CUSTOMER CIRCULATION
      </div>

      <div className="info-grid">
        <div className="info-box">
          <h4>Kit</h4>
          <p className="value" data-testid="bom-cost-sheet-part-no">{bom.part_internal_no}</p>
          <p>{bom.part_description}</p>
        </div>
        <div className="info-box">
          <h4>BOM details</h4>
          <p>Version: <span className="value">V{bom.version_number}{bom.is_active ? ' (current)' : ''}</span></p>
          <p>Child parts: <span className="value">{(pricing.lines || []).length}</span></p>
          <p>Price accepted: <span className="value">{bom.priced_at ? new Date(bom.priced_at).toLocaleString('en-IN') : 'not yet'}</span></p>
        </div>
      </div>

      <table className="items-table" data-testid="bom-cost-sheet-items">
        <thead>
          <tr>
            <th style={{ width: '5%' }}>#</th>
            <th style={{ width: '15%' }}>Part No</th>
            <th style={{ width: '31%' }}>Description</th>
            <th style={{ width: '9%', textAlign: 'right' }}>Qty / kit</th>
            <th style={{ width: '13%' }}>Costing</th>
            <th style={{ width: '13%', textAlign: 'right' }}>Price / pc</th>
            <th style={{ width: '14%', textAlign: 'right' }}>Line cost / kit</th>
          </tr>
        </thead>
        <tbody>
          {(pricing.lines || []).map((line, index) => (
            <tr key={line.child_part_id} data-testid={`bom-cost-sheet-row-${index + 1}`}>
              <td>{index + 1}</td>
              <td style={{ fontFamily: 'Consolas, monospace' }}>{line.child_part_no || '—'}</td>
              <td>{line.child_description}</td>
              <td className="number">{line.quantity_per_kit} {line.unit}</td>
              <td style={{ fontSize: '10px' }}>{line.child_costing_number || '—'}</td>
              <td className="number">{line.child_price_per_piece != null ? money(line.child_price_per_piece) : line.blocked_reason}</td>
              <td className="number">{line.line_cost != null ? money(line.line_cost) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="totals-section">
        <div className="totals-box" data-testid="bom-cost-sheet-totals">
          <div className="totals-row"><span>Components / kit</span><span>{pricing.components_cost_per_kit != null ? money(pricing.components_cost_per_kit) : '—'}</span></div>
          <div className="totals-row"><span>Kitting labour</span><span>{money(pricing.kitting_labor_cost, 2)}</span></div>
          <div className="totals-row"><span>Kitting / packaging</span><span>{money(pricing.kitting_packaging_cost, 2)}</span></div>
          <div className="totals-row total"><span>Kit price / kit</span>
            <span data-testid="bom-cost-sheet-kit-price">{pricing.kit_cost_per_kit != null ? money(pricing.kit_cost_per_kit) : 'BLOCKED'}</span>
          </div>
        </div>
      </div>

      <div className="terms-section">
        <h4>Basis of this kit price</h4>
        <p>Each child part is already costed with its own margin. Kitting adds only the kitting labour and the
          kitting / packaging cost — no further overhead, finance or margin is applied on the kit.</p>
        {pricing.kit_weight_kg && (
          <p data-testid="bom-cost-sheet-weight-reference">
            Internal reference: kit weight {pricing.kit_weight_kg.toFixed(4)} kg
            {pricing.price_per_kg_reference ? ` · ${money(pricing.price_per_kg_reference)} / kg` : ''} (kits are quoted per kit / per set).
          </p>
        )}
        {!pricing.is_priceable && (
          <p data-testid="bom-cost-sheet-blocked">
            Price blocked: {(pricing.missing_children || []).map((child) => `${child.child_part_no || child.child_description} — ${child.reason}`).join('; ')}
          </p>
        )}
      </div>

      <div className="footer">Kit cost sheet generated from BOM {bom.id.slice(0, 8)} on {new Date().toLocaleString('en-IN')}.</div>
    </div>
  );
}
